import React, { useState, useEffect } from 'react';
import EmployeeInfo from './EmployeeInfo';
import EmployeeContact from './EmployeeContact';
import EmployeeFamily from './EmployeeFamily';
import { fetchWithCorpId } from '../utils/api';
import { useParams } from 'react-router-dom';

function EmployeeForm({ mode = 'create', initialData = {}, onSuccess, onCancel }) {
  const { id } = useParams();
  const isEdit = mode === 'edit';
  const [activeTab, setActiveTab] = useState('info');
  const [contacts, setContacts] = useState([]);
  const [employee, setEmployee] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    designation: '',
    role: 'employee',
    status: 'Active',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    email: '',
    phone: '',
    guestCode: '',
    workAuth: '',
    category: '',
    spouseName: '',
    childrenCount: '',
    emergencyContact: '',
  });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchEmployee = async () => {
      if (isEdit && id) {
        setLoading(true);
        try {
          const data = await fetchWithCorpId(`/employees/${id}`);
          setEmployee(data);
        } catch (error) {
          console.error('Error fetching employee:', error);
          setMessage('❌ Error loading employee data: ' + error.message);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchEmployee();
  }, [isEdit, id]);

  // Initialize form data when initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      // Ensure all fields are properly initialized
      setEmployee(prev => ({
        ...prev,
        ...initialData,
        // Ensure dates are in the correct format
        startDate: initialData.startDate ? new Date(initialData.startDate).toISOString().split('T')[0] : prev.startDate,
        endDate: initialData.endDate ? new Date(initialData.endDate).toISOString().split('T')[0] : '',
        // Ensure status has a default value
        status: initialData.status || 'Active',
        role: initialData.role || 'employee',
        // Ensure other fields have default values if not provided
        firstName: initialData.firstName || '',
        middleName: initialData.middleName || '',
        lastName: initialData.lastName || '',
        designation: initialData.designation || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        guestCode: initialData.guestCode || '',
        workAuth: initialData.workAuth || '',
        category: initialData.category || '',
        spouseName: initialData.spouseName || '',
        childrenCount: initialData.childrenCount || '',
        emergencyContact: initialData.emergencyContact || '',
      }));
      // Initialize contacts if they exist
      if (initialData.contacts && Array.isArray(initialData.contacts)) {
        setContacts(initialData.contacts);
      }
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmployee(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = isEdit
      ? `/employees/${employee.id}`
      : '/employees';
    
    try {
      const res = await fetchWithCorpId(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...employee, contacts }),
      });

      if (res) {
        setMessage(`✅ Employee ${isEdit ? 'updated' : 'created'} successfully!`);
        setMessageType('success');
        setTimeout(() => {
          if (onSuccess) onSuccess();
        }, 1000);
      } else {
        setMessage(`❌ Failed to ${isEdit ? 'update' : 'create'} employee: Unknown error`);
        setMessageType('error');
      }
    } catch (err) {
      setMessage(`❌ Error: ${err.message}`);
      setMessageType('error');
    }
  };

  const tabButtonStyle = (tab) => ({
    padding: '10px 20px',
    marginRight: '10px',
    cursor: 'pointer',
    border: activeTab === tab ? '2px solid #007bff' : '1px solid #ccc',
    borderBottom: activeTab === tab ? '2px solid #007bff' : '1px solid transparent',
    borderRadius: '4px 4px 0 0',
    backgroundColor: activeTab === tab ? '#f0f8ff' : '#f9f9f9',
    color: activeTab === tab ? '#007bff' : '#333',
    fontWeight: activeTab === tab ? 'bold' : 'normal',
    transition: 'all 0.2s ease'
  });

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', marginBottom: '20px' }}>
        <button type="button" style={tabButtonStyle('info')} onClick={() => setActiveTab('info')}>Info</button>
        <button type="button" style={tabButtonStyle('contact')} onClick={() => setActiveTab('contact')}>Contact</button>
        <button type="button" style={tabButtonStyle('family')} onClick={() => setActiveTab('family')}>Family</button>
      </div>

      {activeTab === 'info' && (
        <EmployeeInfo employee={employee} handleChange={handleChange} />
      )}

      {activeTab === 'contact' && (
        <EmployeeContact contacts={contacts} setContacts={setContacts} />
      )}

      {activeTab === 'family' && (
        <EmployeeFamily employee={employee} handleChange={handleChange} />
      )}

      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        <button 
          type="submit" 
          style={{ 
            padding: '10px 20px',
            height: '40px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            minWidth: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 0
          }}
        >
          Save
        </button>
        {onCancel && (
          <button 
            type="button" 
            onClick={onCancel} 
            style={{ 
              padding: '10px 20px',
              height: '40px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              minWidth: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0
            }}
          >
            Cancel
          </button>
        )}
      </div>

      {message && (
        <div style={{
          marginTop: '12px',
          padding: '10px',
          borderRadius: '4px',
          backgroundColor: messageType === 'success' ? '#d4edda' : '#f8d7da',
          color: messageType === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${messageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message}
        </div>
      )}
    </form>
  );
}

export default EmployeeForm;
